import { Box, Flex } from 'components';
import { P } from 'components/Typography/Typography';
import { Button, TextField } from 'components/_form';
import { t } from 'i18next';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  getTenantDatabase,
  createTenantDatabase,
  updateTenantDatabase,
  verifyTenantDatabase,
  migrateTenantDatabase
} from 'api/tenantDatabases';
import { companyKeys, tenantDatabaseKeys } from 'api/queryKeys';
import { useQueryClient } from '@tanstack/react-query';
import { useMainCompanyQuery } from 'hooks/queries/useMainCompanyQuery';
import { useTenantDatabaseQuery } from 'hooks/queries/useTenantDatabaseQuery';
import type { ITenantDatabase, ITenantDatabaseForm, IHostDiagnostics } from 'types/tenantDatabase';
import type { ICompany } from 'types/company';

const emptyForm: ITenantDatabaseForm = {
  name: '',
  id_code: '',
  database_host: '',
  database_port: 5432,
  database_name: '',
  database_username: '',
  database_password: ''
};

export const StorageForm = () => {
  const queryClient = useQueryClient();
  const { data: mainCompany = null } = useMainCompanyQuery();
  const tdId = mainCompany?.tenant_database_id ?? null;
  const { data: tenantDbResponse, isLoading: loading } =
    useTenantDatabaseQuery(tdId);
  const [config, setConfig] = useState<ITenantDatabase | null>(null);
  const [form, setForm] = useState<ITenantDatabaseForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [migrating, setMigrating] = useState(false);

  useEffect(() => {
    if (!tdId) {
      setConfig(null);
      setForm({ ...emptyForm });
      return;
    }
    if (!tenantDbResponse?.tenant_database) {
      setConfig(null);
      setForm({ ...emptyForm });
      return;
    }
    const forCompany = tenantDbResponse.tenant_database;
    setConfig(forCompany);
    setForm({
      name: forCompany.name ?? '',
      id_code: forCompany.id_code ?? '',
      database_host: forCompany.database_config?.host ?? '',
      database_port: forCompany.database_config?.port ?? 5432,
      database_name: forCompany.database_config?.database ?? '',
      database_username: forCompany.database_config?.username ?? '',
      database_password: ''
    });
  }, [tenantDbResponse, tdId]);

  const isProvisioningInProgress = !!(
    config?.id &&
    !config.active &&
    (config.provisioning_step === 'queued' || config.provisioning_step === 'migrating')
  );

  useEffect(() => {
    if (!isProvisioningInProgress || !config?.id) return;
    const interval = setInterval(async () => {
      try {
        const res = await getTenantDatabase(config.id);
        const td = res.tenant_database;
        setConfig(td);
        if (td.active || td.provisioning_error) clearInterval(interval);
      } catch {
        // ignore poll errors
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [isProvisioningInProgress, config?.id]);

  const handleSave = async () => {
    if (!form.name || !form.id_code || !form.database_host || !form.database_name || !form.database_username) {
      toast.error('Wypełnij wymagane pola: nazwa, kod tenanta, host, baza, login do bazy.');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (config?.id) {
        if (!payload.database_password) delete (payload as Partial<ITenantDatabaseForm>).database_password;
        const res = await updateTenantDatabase(config.id, payload);
        const td = res.tenant_database;
        setConfig(prev => prev ? { ...prev, ...td, database_config: td.database_config ?? prev.database_config } : null);
        setForm(f => ({
          ...f,
          name: td.name,
          id_code: td.id_code,
          database_host: td.database_config?.host ?? f.database_host,
          database_port: td.database_config?.port ?? f.database_port,
          database_name: td.database_config?.database ?? f.database_name,
          database_username: td.database_config?.username ?? f.database_username
          // hasło nie nadpisujemy
        }));
        toast.success(t('common.success') || 'Zapisano.');
      } else {
        const res = await createTenantDatabase(payload);
        const td = res.tenant_database;
        setConfig(td);
        setForm(f => ({
          ...f,
          name: td.name,
          id_code: td.id_code,
          database_host: td.database_config?.host ?? f.database_host,
          database_port: td.database_config?.port ?? f.database_port,
          database_name: td.database_config?.database ?? f.database_name,
          database_username: td.database_config?.username ?? f.database_username,
          database_password: ''
        }));
        queryClient.invalidateQueries({ queryKey: companyKeys.main() });
        if (td?.id) {
          queryClient.invalidateQueries({ queryKey: tenantDatabaseKeys.detail(td.id) });
        }
        toast.success(t('common.success') || 'Utworzono.');
      }
    } catch (e: unknown) {
      const err = e as { data?: { errors?: string[] }; message?: string };
      const msg = err?.data?.errors?.length ? err.data.errors.join(', ') : (err?.message || 'Błąd zapisu');
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleVerify = async () => {
    if (!config?.id) {
      toast.error('Zapisz konfigurację najpierw.');
      return;
    }
    setVerifying(true);
    try {
      const result = await verifyTenantDatabase(config.id);
      setConfig(prev => prev ? { ...prev, host_diagnostics: result as IHostDiagnostics } : null);
      if (result.overall_ok) {
        toast.success('Weryfikacja zakończona pomyślnie.');
      } else {
        const failed = result.stages?.find(s => !s.ok);
        toast.warning(failed ? failed.message : 'Weryfikacja nie powiodła się.');
      }
    } catch (e: unknown) {
      const err = e as { message?: string };
      toast.error(err?.message || 'Błąd weryfikacji.');
    } finally {
      setVerifying(false);
    }
  };

  const handleMigrate = async () => {
    if (!config?.id) return;
    const diag = config.host_diagnostics;
    if (!diag?.overall_ok) {
      toast.error('Najpierw uruchom weryfikację i upewnij się, że wszystkie etapy są OK.');
      return;
    }
    setMigrating(true);
    try {
      await migrateTenantDatabase(config.id);
      toast.success('Migracja w toku. Stan zaktualizuje się automatycznie.');
      const res = await getTenantDatabase(config.id);
      setConfig(res.tenant_database);
    } catch (e: unknown) {
      const err = e as { message?: string };
      toast.error(err?.message || 'Błąd uruchomienia migracji.');
    } finally {
      setMigrating(false);
    }
  };

  const diagnostics: IHostDiagnostics | null = config?.host_diagnostics ?? null;
  const canMigrate =
    !!config?.id && !!diagnostics?.overall_ok && !config.active && !isProvisioningInProgress;

  if (loading) {
    return <P variant="body" p={4}>Ładowanie…</P>;
  }

  return (
    <Flex p={4} flexDirection="column" maxWidth="900px">
      <P variant="body" mb={3}>
        {t('administration.Along with the subscription, disk space for data research and the full amount of space for storing files - photos, documents, attachments, etc. are provided. You can provide any large storage for files on your servers or in the cloud service, providing access data below.')}
      </P>
      <P variant="body" fontWeight={700} mb={2}>
        {t('administration.Storage for storing files - photos, documents, switches, etc.:')}
      </P>

      <Box mb={4} p={3} style={{ backgroundColor: '#f5f5f5', borderRadius: 8 }}>
        <P variant="body" fontWeight={700} mb={2}>
          Konfiguracja bazy tenanta
        </P>
        <Flex flexDirection="column" gap="8px">
          <TextField
            label="Nazwa"
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            placeholder={config ? '' : 'nazwa firmy'}
          />
          <TextField
            label="Kod tenanta (id_code)"
            value={form.id_code}
            onChange={e => setForm(f => ({ ...f, id_code: e.target.value }))}
            placeholder={config ? '' : 'kod tenanta'}
          />
          <TextField
            label="Host bazy"
            value={form.database_host}
            onChange={e => setForm(f => ({ ...f, database_host: e.target.value }))}
            placeholder={config ? '' : 'host'}
          />
          <TextField
            label="Port"
            type="number"
            value={String(form.database_port)}
            onChange={e => setForm(f => ({ ...f, database_port: e.target.value || 5432 }))}
            placeholder={config ? '' : '5432'}
          />
          <TextField
            label="Nazwa bazy (database_name)"
            value={form.database_name}
            onChange={e => setForm(f => ({ ...f, database_name: e.target.value }))}
            placeholder={config ? '' : 'nazwa bazy'}
          />
          <TextField
            label="Nazwa użytkownika"
            value={form.database_username}
            onChange={e => setForm(f => ({ ...f, database_username: e.target.value }))}
            placeholder={config ? '' : 'nazwa użytkownika'}
            autoComplete="off"
          />
          <TextField
            label={config ? 'Nowe hasło' : 'Hasło do bazy'}
            type="password"
            value={form.database_password}
            onChange={e => setForm(f => ({ ...f, database_password: e.target.value }))}
            placeholder={config ? 'zostaw puste, aby nie zmieniać' : 'hasło'}
            autoComplete="new-password"
          />
          <Flex gap="8px" mt={2}>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (config ? 'Zapisywanie…' : 'Tworzenie…') : (config ? 'Zapisz' : 'Utwórz')}
            </Button>
          </Flex>
        </Flex>
      </Box>

      {config?.id && (
        <Box mb={4} p={3} style={{ background: 'white', border: '1px solid #eee', borderRadius: 8 }}>
          <P variant="body" fontWeight={700} mb={2}>Weryfikacja i migracja</P>

          {config.active ? (
            <Box mb={3} p={2} style={{ backgroundColor: '#e8f5e9', borderRadius: 4 }}>
              <P variant="body2" fontWeight={600}>Baza aktywna</P>
            </Box>
          ) : isProvisioningInProgress ? (
            <Box mb={3} p={2} style={{ backgroundColor: '#fff3e0', borderRadius: 4 }}>
              <P variant="body2" fontWeight={600}>Migracja w toku…</P>
              <P variant="body2" color="grey">Stan odświeża się co kilka sekund.</P>
            </Box>
          ) : null}

          {diagnostics && (
            <Box mb={3}>
              <P variant="body2" fontWeight={600} mb={1}>
                Ostatni wynik weryfikacji ({diagnostics.checked_at ? new Date(diagnostics.checked_at).toLocaleString('pl-PL') : ''})
              </P>
              {diagnostics.stages?.map((stage, i) => (
                <Flex key={i} alignItems="center" gap="8px" py="4px">
                  <Box
                    width="12px"
                    height="12px"
                    style={{ borderRadius: '50%', backgroundColor: stage.ok ? '#4caf50' : '#f44336' }}
                  />
                  <P variant="body2"><strong>{stage.name}</strong>: {stage.message}</P>
                </Flex>
              ))}
              <P variant="body2" mt={1}>
                {diagnostics.overall_ok ? 'Wszystkie etapy OK.' : 'Weryfikacja nie powiodła się.'}
              </P>
            </Box>
          )}

          {config.provisioning_error && (
            <Box mb={2} p={2} style={{ backgroundColor: '#f44336', color: 'white', borderRadius: 4 }}>
              <P variant="body2">Błąd migracji: {config.provisioning_error}</P>
            </Box>
          )}

          <Flex gap="8px" flexWrap="wrap">
            <Button onClick={handleVerify} disabled={verifying}>
              {verifying ? 'Sprawdzam…' : (t('administration.Check the connection') || 'Zweryfikuj')}
            </Button>
            <Button
              variant="eucalyptus"
              onClick={handleMigrate}
              disabled={!canMigrate || migrating}
            >
              {migrating ? 'Uruchamiam…' : (t('administration.Migrate files') || 'Migruj')}
            </Button>
          </Flex>
          {!diagnostics?.overall_ok && (
            <P variant="body2" color="grey" mt={2}>
              Przycisk „Migruj” będzie dostępny po pomyślnej weryfikacji (oba etapy: sieć i baza).
            </P>
          )}
        </Box>
      )}
    </Flex>
  );
};
